import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';

const NO_PAGE_BIND = new Set(['payment']);

const BUILT_IN_DEFS = [
  { page_slug: '__global__',  name: '🌐 Global',          url: '/' },
  { page_slug: 'home',        name: '🏠 Startseite',      url: '/' },
  { page_slug: 'detail',      name: '📄 Rezept-Detail',   url: '/recipe/1' },
  { page_slug: 'upload',      name: '⬆ Upload',           url: '/upload' },
  { page_slug: 'admin',       name: '⚙ Admin',            url: '/admin' },
  { page_slug: 'impressum',   name: '§ Impressum',        url: '/impressum' },
  { page_slug: 'datenschutz', name: '🔒 Datenschutz',     url: '/datenschutz' },
  { page_slug: 'agb',         name: '📋 AGB',             url: '/agb' },
  { page_slug: 'kontakt',     name: '✉ Kontakt',          url: '/kontakt' },
];
const BUILT_IN_SLUGS = BUILT_IN_DEFS.map(d => d.page_slug);
const PAGE_URLS = Object.fromEntries(BUILT_IN_DEFS.map(d => [d.page_slug, d.url]));

export default function Pages() {
  const [layouts, setLayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [modal, setModal]         = useState(false);
  const [form, setForm]           = useState({ name: '', slug: '' });
  const [saving, setSaving]       = useState(false);
  const [formError, setFormError] = useState('');
  const [allModules, setAllModules]   = useState([]);
  const [selectedMods, setSelectedMods] = useState([]);
  const [confirmDeleteLayout, setConfirmDeleteLayout] = useState(null);

  useEffect(() => {
    load();
    api.getAllModules().then(d => setAllModules((d.modules || []).filter(m => !NO_PAGE_BIND.has(m.name)))).catch(() => {});
  }, []);

  async function load() {
    setLoading(true);
    try {
      const d = await api.getLayouts();
      const fromApi = d.layouts || [];
      const apiMap = Object.fromEntries(fromApi.map(l => [l.page_slug, l]));
      // Built-ins immer anzeigen, API-Daten überschreiben Platzhalter
      const builtins = BUILT_IN_DEFS.map(def => apiMap[def.page_slug] || { ...def, updated_at: null });
      // Custom-Seiten anhängen (nicht in BUILT_IN_SLUGS)
      const custom = fromApi.filter(l => !BUILT_IN_SLUGS.includes(l.page_slug));
      setLayouts([...builtins, ...custom]);
    } catch(e) {
      setError('Fehler beim Laden der Seiten');
    } finally {
      setLoading(false);
    }
  }

  async function handleDelete(layout) {
    setConfirmDeleteLayout(null);
    setError('');
    try {
      await api.deleteLayout(layout.page_slug);
      await load();
    } catch(e) {
      setError(e.message || 'Fehler beim Löschen');
    }
  }

  function autoSlug(name) {
    return name.toLowerCase().trim()
      .replace(/ä/g,'ae').replace(/ö/g,'oe').replace(/ü/g,'ue')
      .replace(/[^a-z0-9]+/g,'-').replace(/^-+|-+$/g,'');
  }

  async function handleCreate(e) {
    e.preventDefault();
    setFormError('');
    const name = form.name.trim();
    const slug = form.slug.trim().toLowerCase().replace(/[^a-z0-9-]/g,'');
    if (!name) { setFormError('Seitenname erforderlich'); return; }
    if (!slug) { setFormError('Slug erforderlich'); return; }
    if (BUILT_IN_SLUGS.includes(slug)) { setFormError('Dieser Slug ist reserviert'); return; }
    const existing = layouts.find(l => l.page_slug === slug);
    if (existing) { setFormError('Slug bereits vergeben'); return; }
    setSaving(true);
    try {
      await api.saveLayout(slug, name, {}, false);
      // Modul-Bindungen speichern
      await Promise.all(selectedMods.map(modName =>
        api.getModulePages().then(d => {
          const current = (d.module_pages || {})[modName] || [];
          if (!current.includes(slug)) {
            return api.setModulePages(modName, [...current, slug]);
          }
        }).catch(() => {})
      ));
      setModal(false);
      setForm({ name: '', slug: '' });
      setSelectedMods([]);
      await load();
    } catch(e) {
      setFormError(e.message || 'Fehler beim Erstellen');
    } finally {
      setSaving(false);
    }
  }

  const s = {
    wrap:    { padding: '28px 32px', maxWidth: 1000 },
    h1:      { fontSize: '1.4rem', color: 'var(--admin-text)', fontFamily: 'Inter,sans-serif', fontWeight: 600, marginBottom: 6 },
    sub:     { fontSize: '.78rem', color: 'var(--admin-text-dim)', marginBottom: 24 },
    topRow:  { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
    secLbl:  { fontSize: '.7rem', fontWeight: 600, color: 'var(--admin-text-dim)', textTransform: 'uppercase', letterSpacing: '.08em' },
    btn:     { background: 'var(--admin-btn-primary)', border: 'none', color: '#fff', borderRadius: 5, padding: '7px 16px', fontSize: '.75rem', fontWeight: 600, cursor: 'pointer' },
    table:   { width: '100%', borderCollapse: 'collapse', fontSize: '.78rem', background: 'var(--admin-bg)', border: '1px solid var(--admin-border)', borderRadius: 8, overflow: 'hidden' },
    th:      { padding: '10px 16px', textAlign: 'left', color: 'var(--admin-text-dim)', fontWeight: 600, background: 'var(--admin-bg-dark)', borderBottom: '1px solid var(--admin-border)' },
    tdFn:    (i) => ({ padding: '10px 16px', borderBottom: '1px solid var(--admin-border)', background: i % 2 === 0 ? 'var(--admin-bg)' : 'var(--admin-bg-alt)', verticalAlign: 'middle' }),
    badge:   (builtin) => ({ display: 'inline-block', padding: '2px 8px', borderRadius: 20, fontSize: '.62rem', fontWeight: 600, background: builtin ? 'color-mix(in srgb, var(--admin-link) 10%, transparent)' : 'color-mix(in srgb, var(--admin-success) 10%, transparent)', color: builtin ? 'var(--admin-link)' : 'var(--admin-success)', border: `1px solid ${builtin ? 'var(--admin-link)' : 'var(--admin-success)'}` }),
    btnEdt:  { background: 'none', border: '1px solid var(--admin-border)', color: 'var(--admin-text-muted)', borderRadius: 4, padding: '3px 9px', fontSize: '.68rem', textDecoration: 'none', display: 'inline-block' },
    btnDel:  { background: 'none', border: '1px solid var(--admin-danger)', color: 'var(--admin-danger)', borderRadius: 4, padding: '3px 9px', fontSize: '.68rem', cursor: 'pointer', marginLeft: 5 },
    overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 },
    mbox:    { background: 'var(--admin-bg)', border: '1px solid var(--admin-border)', borderRadius: 10, padding: 28, width: 380 },
    lbl:     { display: 'block', fontSize: '.68rem', color: 'var(--admin-text-muted)', marginBottom: 4, marginTop: 12 },
    inp:     { width: '100%', background: 'var(--admin-bg-input)', border: '1px solid var(--admin-border)', borderRadius: 5, color: 'var(--admin-text)', padding: '7px 10px', fontSize: '.78rem', boxSizing: 'border-box' },
    errBox:  { background: 'color-mix(in srgb, var(--admin-danger) 10%, transparent)', border: '1px solid var(--admin-danger)', borderRadius: 5, padding: '7px 12px', fontSize: '.73rem', color: 'var(--admin-danger)', marginTop: 12 },
  };

  return (
    <div style={s.wrap}>
      <h1 style={s.h1}>Seiten-Verwaltung</h1>
      <p style={s.sub}>Seiten anlegen, Inhalte bearbeiten und löschen.</p>

      {error && <div style={{...s.errBox, marginBottom: 16}}>{error}</div>}

      {confirmDeleteLayout && (
        <div style={{...s.errBox, marginBottom: 16, display:'flex', alignItems:'center', gap:12, justifyContent:'space-between'}}>
          <span>Seite <strong>„{confirmDeleteLayout.name}"</strong> wirklich löschen?</span>
          <div style={{display:'flex', gap:8}}>
            <button style={{background:'var(--admin-danger)',border:'none',color:'#fff',borderRadius:4,padding:'4px 12px',fontSize:'.72rem',cursor:'pointer'}}
              onClick={() => handleDelete(confirmDeleteLayout)}>Ja, löschen</button>
            <button style={{background:'none',border:'1px solid var(--admin-border)',color:'var(--admin-text-muted)',borderRadius:4,padding:'4px 12px',fontSize:'.72rem',cursor:'pointer'}}
              onClick={() => setConfirmDeleteLayout(null)}>Abbrechen</button>
          </div>
        </div>
      )}

      <div style={s.topRow}>
        <span style={s.secLbl}>Alle Seiten ({layouts.length})</span>
        <button style={s.btn} onClick={() => { setModal(true); setForm({ name:'', slug:'' }); setFormError(''); setSelectedMods([]); }}>
          + Neue Seite
        </button>
      </div>

      {loading ? (
        <p style={{color:'var(--admin-text-dim)',fontSize:'.78rem'}}>Lade...</p>
      ) : (
        <table style={s.table}>
          <thead>
            <tr>
              {['Name','Slug','Typ','Zuletzt geändert','Seite','Aktionen'].map(h => (
                <th key={h} style={s.th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {layouts.map((l, i) => {
              const isBuiltin = BUILT_IN_SLUGS.includes(l.page_slug);
              return (
                <tr key={l.page_slug}>
                  <td style={{...s.tdFn(i), color: 'var(--admin-text)', fontWeight: 600}}>{l.name}</td>
                  <td style={{...s.tdFn(i), fontFamily: 'monospace', color: 'var(--admin-text-muted)', fontSize: '.72rem'}}>{l.page_slug}</td>
                  <td style={s.tdFn(i)}><span style={s.badge(isBuiltin)}>{isBuiltin ? 'Intern' : 'Custom'}</span></td>
                  <td style={{...s.tdFn(i), color: 'var(--admin-text-dim)'}}>
                    {l.updated_at ? new Date(l.updated_at).toLocaleString('de-DE') : '—'}
                  </td>
                  <td style={s.tdFn(i)}>
                    <a
                      href={PAGE_URLS[l.page_slug] || `/page/${l.page_slug}`}
                      target="_blank" rel="noopener noreferrer"
                      style={{...s.btnEdt, borderColor:'var(--admin-success)', color:'var(--admin-success)'}}
                    >🌐 Ansehen</a>
                  </td>
                  <td style={s.tdFn(i)}>
                    <Link to={`/admin/content/${l.page_slug}`} style={{ ...s.btnEdt, borderColor: 'var(--admin-link)', color: 'var(--admin-link)' }}>✏ Inhalt</Link>
                    {!isBuiltin && (
                      <button style={s.btnDel} onClick={() => { setError(''); setConfirmDeleteLayout(l); }}>✕ Löschen</button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {modal && (
        <div style={s.overlay} onClick={e => e.target === e.currentTarget && setModal(false)}>
          <div style={s.mbox}>
            <div style={{fontSize:'1rem', color:'var(--admin-text)', fontWeight:600, marginBottom:4}}>📄 Neue Seite</div>
            <div style={{fontSize:'.68rem',color:'var(--admin-text-dim)',marginBottom:16}}>Erstellt eine neue Seite.</div>
            <form onSubmit={handleCreate}>
              <label style={{...s.lbl, marginTop:0}}>Seitenname *</label>
              <input style={s.inp} value={form.name} placeholder="z.B. Über uns"
                onChange={e => setForm(p => ({ ...p, name: e.target.value, slug: autoSlug(e.target.value) }))} />
              <label style={s.lbl}>Slug *</label>
              <input style={{...s.inp, fontFamily:'monospace'}} value={form.slug} placeholder="z.B. about"
                onChange={e => setForm(p => ({ ...p, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g,'') }))} />
              <div style={{fontSize:'.6rem',color:'var(--admin-text-dim)',marginTop:3}}>Nur Kleinbuchstaben, Zahlen und Bindestriche.</div>

              {allModules.length > 0 && (
                <div style={{marginTop:14}}>
                  <div style={{fontSize:'.68rem',color:'var(--admin-text-muted)',marginBottom:6,fontWeight:600}}>Module auf dieser Seite</div>
                  <div style={{display:'flex',flexWrap:'wrap',gap:6}}>
                    {allModules.map(mod => (
                      <label key={mod.name} style={{
                        display:'flex', alignItems:'center', gap:6, cursor:'pointer',
                        background: selectedMods.includes(mod.name) ? 'color-mix(in srgb, var(--admin-btn-primary) 15%, transparent)' : 'color-mix(in srgb, var(--admin-text-dim) 6%, transparent)',
                        border:`1px solid ${selectedMods.includes(mod.name) ? 'var(--admin-btn-primary)' : 'var(--admin-border)'}`,
                        borderRadius:5, padding:'5px 10px', fontSize:'.75rem',
                        color: selectedMods.includes(mod.name) ? 'var(--admin-link)' : 'var(--admin-text-dim)',
                        userSelect:'none',
                      }}>
                        <input type="checkbox" checked={selectedMods.includes(mod.name)}
                          onChange={() => setSelectedMods(prev =>
                            prev.includes(mod.name) ? prev.filter(m => m !== mod.name) : [...prev, mod.name]
                          )}
                          style={{accentColor:'var(--admin-btn-primary)'}}
                        />
                        {mod.label}
                      </label>
                    ))}
                  </div>
                  <div style={{fontSize:'.6rem',color:'var(--admin-text-dim)',marginTop:5}}>
                    Kein Haken = Modul überall sichtbar. Haken = Modul nur auf dieser Seite.
                  </div>
                </div>
              )}

              {formError && <div style={s.errBox}>{formError}</div>}
              <div style={{display:'flex',gap:8,marginTop:20,justifyContent:'flex-end'}}>
                <button type="button" style={{background:'none',border:'1px solid var(--admin-border)',color:'var(--admin-text-muted)',borderRadius:5,padding:'7px 16px',fontSize:'.75rem',cursor:'pointer'}}
                  onClick={() => setModal(false)}>Abbrechen</button>
                <button type="submit" style={{background:'var(--admin-btn-primary)',border:'none',color:'#fff',borderRadius:5,padding:'7px 16px',fontSize:'.75rem',fontWeight:600,cursor:'pointer'}}
                  disabled={saving}>{saving ? 'Erstellt…' : '✓ Erstellen'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
